import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * Reads the next N upcoming milestones for the signed-in user.
 *
 * Source of truth: `project_item` rows where `kind='milestone'` and
 * `data->>'date' >= today`, joined to `project` for the project label,
 * sorted ascending by date, limit N.
 *
 * Returns the same shape the hardcoded fallback (src/data/milestones.ts) exposes
 * so CountdownBand consumers can swap.
 */

export type UpcomingMilestone = {
  id: string;
  title: string;
  date: string;        // ISO yyyy-mm-dd
  project: string;     // project name (joined)
  projectId: string | null;  // FK so the card can deep-link to /projects/:id
  importance: "primary" | "secondary";
  daysUntil: number;
};

export function useUpcomingMilestones(n = 3) {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["upcoming_milestones", userId, n],
    enabled: Boolean(userId),
    staleTime: 60 * 60_000,
    queryFn: async (): Promise<UpcomingMilestone[]> => {
      if (!userId) return [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().slice(0, 10);

      const { data, error } = await supabase
        .from("project_item")
        .select("id, title, data, project_id, project:project!inner(name, priority, status)")
        .eq("user_id", userId)
        .eq("kind", "milestone")
        .gte("data->>date", todayStr)
        .in("project.status", ["planned", "active"])
        .order("data->>date", { ascending: true })
        .limit(n);
      if (error) throw error;

      const rows = (data ?? []) as unknown as Array<{
        id: string;
        title: string;
        data: { date?: string; hard?: boolean; detail?: string };
        project_id: string | null;
        project: { name: string; priority: string; status: string } | null;
      }>;

      return rows
        .filter((r) => Boolean(r.data?.date))
        .map((r) => {
          const d = new Date(r.data.date! + "T00:00:00");
          const daysUntil = Math.round(
            (d.getTime() - today.getTime()) / 86_400_000,
          );
          return {
            id: r.id,
            title: r.title,
            date: r.data.date!,
            project: r.project?.name ?? "—",
            projectId: r.project_id ?? null,
            importance:
              r.project?.priority === "critical" || r.project?.priority === "high"
                ? "primary"
                : "secondary",
            daysUntil,
          };
        });
    },
  });
}
